// WP-3.1 (M1 PT-2, S-20 `/parties/:id`): header (name/roles/aliases/phone) + stat strip +
// tabs. No shared tab primitive exists yet in this codebase (checked: only SettingsLayout's
// nested-route subnav comes close, a different shape) — a plain button row + conditional
// render is the whole UI a one-off tab bar needs, so no new dependency is pulled in for it.
// Quotations went real in WP-3.2, Projects in WP-3.3, and Overview's own financial timeline
// in WP-3.7 (all three gaps flagged in WP-3.1's own CHANGELOG entry, each closed in its own WP)
// — the shared `TimelineFeed` (party-scoped, TL-1's own "three scopes") replaces the party's
// own linked-record counts as the tab's real content.
import { formatMoney } from '@pfos/engine';
import type { CurrencyAmount, PartyResponse } from '@pfos/shared';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Amount } from '../components/Amount';
import { EntityTagSection } from '../components/EntityTagSection';
import { NotesSection } from '../components/NotesSection';
import { TimelineFeed } from '../components/TimelineFeed';
import { ghostButtonInlineClass } from '../components/FormKit';
import {
  useArchiveParty,
  useConfirmParty,
  useParty,
  usePartyProfile,
  useUnarchiveParty,
} from '../lib/parties';
import { computeFinalValueMinor, useProjects } from '../lib/projects';
import { useQuotations } from '../lib/quotations';
import { useRps } from '../lib/rps';
import { useTpfCases } from '../lib/tpf';
import { useTransactions } from '../lib/transactions';

const TABS = [
  'overview',
  'projects',
  'quotations',
  'owed',
  'tpf',
  'transactions',
  'notes',
] as const;
type Tab = (typeof TABS)[number];
const TAB_LABELS: Record<Tab, string> = {
  overview: 'Overview',
  projects: 'Projects',
  quotations: 'Quotations',
  owed: 'Owed',
  tpf: 'Third-party',
  transactions: 'Transactions',
  notes: 'Notes & files',
};

function amountList(amounts: CurrencyAmount[]): string {
  if (amounts.length === 0) return '—';
  return amounts.map((a) => `${a.currency} ${(a.amountMinor / 100).toLocaleString()}`).join(', ');
}

function StatStrip({
  profile,
}: {
  profile: NonNullable<ReturnType<typeof usePartyProfile>['data']>;
}): React.JSX.Element {
  const stats = [
    { label: 'Received total', value: amountList(profile.receivedTotal) },
    { label: 'Paid total', value: amountList(profile.paidTotal) },
    { label: 'Owed to me', value: amountList(profile.receivableMinor) },
    { label: 'I owe', value: amountList(profile.payableMinor) },
    { label: 'Holding (TPF)', value: amountList(profile.tpfHeldMinor) },
  ];
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
      {stats.map((s) => (
        <div
          key={s.label}
          className="rounded-[var(--radius-card)] border border-border bg-surface p-3"
        >
          <p className="text-xs text-muted">{s.label}</p>
          <p className="text-sm font-medium text-ink">{s.value}</p>
        </div>
      ))}
    </div>
  );
}

function OwedTab({ rpIds }: { rpIds: string[] }): React.JSX.Element {
  const { data: rps } = useRps();
  const linked = (rps ?? []).filter((r) => rpIds.includes(r.id));
  if (linked.length === 0)
    return <p className="text-sm text-muted">No receivables or payables linked to this party.</p>;
  return (
    <ul className="divide-y divide-border">
      {linked.map((r) => (
        <li key={r.id} className="flex items-center justify-between py-2 text-sm">
          <span className="text-ink">
            {r.kind === 'receivable' ? 'Owed to me' : 'I owe'} — {r.reason}
          </span>
          <Amount minorUnits={r.amountMinor - r.settledMinor} currency={r.currency} />
        </li>
      ))}
    </ul>
  );
}

function TpfTab({ tpfCaseIds }: { tpfCaseIds: string[] }): React.JSX.Element {
  const { data: cases } = useTpfCases();
  const linked = (cases ?? []).filter((c) => tpfCaseIds.includes(c.id));
  if (linked.length === 0)
    return <p className="text-sm text-muted">No third-party fund cases linked to this party.</p>;
  return (
    <ul className="divide-y divide-border">
      {linked.map((c) => (
        <li key={c.id} className="flex items-center justify-between py-2 text-sm">
          <span className="text-ink">{c.status === 'open' ? 'Open' : 'Closed'} case</span>
          <Amount minorUnits={c.owedBackMinor} currency={c.currency} />
        </li>
      ))}
    </ul>
  );
}

function TransactionsTab({ transactionIds }: { transactionIds: string[] }): React.JSX.Element {
  const { data: transactions } = useTransactions();
  const linked = (transactions ?? []).filter((t) => transactionIds.includes(t.id));
  if (linked.length === 0)
    return <p className="text-sm text-muted">No transactions linked to this party.</p>;
  return (
    <ul className="divide-y divide-border">
      {linked.map((t) => (
        <li key={t.id} className="flex items-center justify-between py-2 text-sm">
          <Link to={`/tx/${t.id}`} className="text-primary hover:underline">
            {t.type === 'income' ? 'Income' : 'Expense'}
            {t.note !== undefined ? ` — ${t.note}` : ''}
          </Link>
          <Amount minorUnits={t.amountMinor} currency={t.currency} />
        </li>
      ))}
    </ul>
  );
}

function ProjectsTab({ projectIds }: { projectIds: string[] }): React.JSX.Element {
  const { data: projects } = useProjects();
  const linked = (projects ?? []).filter((p) => projectIds.includes(p.id));
  if (linked.length === 0)
    return <p className="text-sm text-muted">No projects linked to this party.</p>;
  return (
    <ul className="divide-y divide-border">
      {linked.map((p) => (
        <li key={p.id} className="flex items-center justify-between py-2 text-sm">
          <Link to={`/projects/${p.id}`} className="text-primary hover:underline">
            {p.title} · <span className="capitalize">{p.status}</span>
          </Link>
          <span className="text-ink">
            {formatMoney({ currency: p.currency, minorUnits: computeFinalValueMinor(p) })}
          </span>
        </li>
      ))}
    </ul>
  );
}

function QuotationsTab({ quotationIds }: { quotationIds: string[] }): React.JSX.Element {
  const { data: quotations } = useQuotations();
  const linked = (quotations ?? []).filter((q) => quotationIds.includes(q.id));
  if (linked.length === 0)
    return <p className="text-sm text-muted">No quotations linked to this party.</p>;
  return (
    <ul className="divide-y divide-border">
      {linked.map((q) => (
        <li key={q.id} className="flex items-center justify-between py-2 text-sm">
          <Link to={`/quotes/${q.id}`} className="text-primary hover:underline">
            {q.title} · <span className="capitalize">{q.status}</span>
          </Link>
          <span className="text-ink">
            {q.pricing.kind === 'fixed'
              ? formatMoney({ currency: q.currency, minorUnits: q.pricing.amountMinor })
              : `${formatMoney({ currency: q.currency, minorUnits: q.pricing.minMinor })}–${formatMoney({ currency: q.currency, minorUnits: q.pricing.maxMinor })}`}
          </span>
        </li>
      ))}
    </ul>
  );
}

function PartyHeader({ party }: { party: PartyResponse }): React.JSX.Element {
  const archiveMutation = useArchiveParty();
  const unarchiveMutation = useUnarchiveParty();
  const confirmMutation = useConfirmParty();

  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <Link
          to="/parties"
          className="mb-2 flex items-center gap-1 text-sm text-muted hover:text-ink"
        >
          <ArrowLeft size={14} /> Parties
        </Link>
        <h1 className="text-xl font-semibold text-ink">{party.name}</h1>
        <p className="text-sm text-muted">
          {party.roles.join(', ') || 'No roles set'}
          {party.aliases.length > 0 && ` · aka ${party.aliases.join(', ')}`}
          {party.phone !== undefined && ` · ${party.phone}`}
        </p>
        {party.provisional && (
          <p className="mt-1 text-xs text-warning">
            Unconfirmed — created automatically during capture (PT-6)
          </p>
        )}
        {party.mergedIntoId !== undefined && (
          <p className="mt-1 text-xs text-muted">
            Merged into another party —{' '}
            <Link to={`/parties/${party.mergedIntoId}`} className="text-primary hover:underline">
              view it
            </Link>
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        {party.provisional && (
          <button
            type="button"
            className={ghostButtonInlineClass}
            disabled={confirmMutation.isPending}
            onClick={() => {
              confirmMutation.mutate(party.id);
            }}
          >
            Confirm
          </button>
        )}
        {party.mergedIntoId === undefined &&
          (party.archived ? (
            <button
              type="button"
              className={ghostButtonInlineClass}
              disabled={unarchiveMutation.isPending}
              onClick={() => {
                unarchiveMutation.mutate(party.id);
              }}
            >
              Unarchive
            </button>
          ) : (
            <button
              type="button"
              className={ghostButtonInlineClass}
              disabled={archiveMutation.isPending}
              onClick={() => {
                archiveMutation.mutate(party.id);
              }}
            >
              Archive
            </button>
          ))}
      </div>
    </div>
  );
}

export function PartyDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const [tab, setTab] = useState<Tab>('overview');
  const { data: party, isLoading: partyLoading } = useParty(id);
  const { data: profile, isLoading: profileLoading } = usePartyProfile(id);

  if (partyLoading || profileLoading) return <p className="text-sm text-muted">Loading…</p>;
  if (party === undefined || profile === undefined)
    return <p className="text-sm text-muted">Party not found.</p>;

  return (
    <div>
      <PartyHeader party={party} />
      <StatStrip profile={profile} />
      <div className="mb-4">
        <EntityTagSection entityType="party" entityId={id} />
      </div>

      <div className="mb-4 flex flex-wrap gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            className={`px-3 py-2 text-sm font-medium ${tab === t ? 'border-b-2 border-primary text-primary' : 'text-muted hover:text-ink'}`}
            onClick={() => {
              setTab(t);
            }}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div>
          <p className="mb-3 text-sm text-muted">
            {profile.transactionIds.length} transaction(s), {profile.rpIds.length}{' '}
            receivable/payable(s), {profile.tpfCaseIds.length} TPF case(s).
          </p>
          <TimelineFeed filters={{ partyId: id }} showTypeFilter={false} />
        </div>
      )}
      {tab === 'projects' && <ProjectsTab projectIds={profile.projectIds} />}
      {tab === 'quotations' && <QuotationsTab quotationIds={profile.quotationIds} />}
      {tab === 'owed' && <OwedTab rpIds={profile.rpIds} />}
      {tab === 'tpf' && <TpfTab tpfCaseIds={profile.tpfCaseIds} />}
      {tab === 'transactions' && <TransactionsTab transactionIds={profile.transactionIds} />}
      {tab === 'notes' && <NotesSection entityType="party" entityId={id} />}
    </div>
  );
}
